import {
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import {
  useCallback
} from 'react';

import type {
  ArticleGroupPlacement,
  GetProjectsResponse,
  LoadProjectResponse,
  ProblemDetails,
  SaveProjectParams,
  SaveProjectResponse
} from '../../models';

import { useCustomAppClient } from '../../custom-client.tsx';
import type { ErrorType } from '../../custom-client.tsx';




const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * Get all saved projects ordered newest-first
 */
export const useGetProjectsHook = () => {
        const getProjects = useCustomAppClient<GetProjectsResponse>();

        return useCallback((

 signal?: AbortSignal
) => {


        return getProjects(
          {url: `/projects`, method: 'GET', signal
    },
          );
        }, [getProjects])
      }




export const getGetProjectsQueryKey = () => {
    return [
    `/projects`
    ] as const;
    }


export const useGetProjectsQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>, TError = ErrorType<ProblemDetails>>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetProjectsQueryKey();

  const getProjects =  useGetProjectsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>> = ({ signal }) => getProjects(signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetProjectsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>>
export type GetProjectsQueryError = ErrorType<ProblemDetails>


export function useGetProjects<TData = Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>, TError = ErrorType<ProblemDetails>>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetProjects<TData = Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>, TError = ErrorType<ProblemDetails>>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetProjects<TData = Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>, TError = ErrorType<ProblemDetails>>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetProjects<TData = Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>, TError = ErrorType<ProblemDetails>>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProjectsHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetProjectsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Save the current session as a named project. The BuildingModel is read from the session cache — no IFC re-upload required.
 */
export const useSaveProjectHook = () => {
        const saveProject = useCustomAppClient<SaveProjectResponse>();

        return useCallback((
    articleGroupPlacement: ArticleGroupPlacement[],
    params: SaveProjectParams,
 signal?: AbortSignal
) => {


        return saveProject(
          {url: `/projects`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: articleGroupPlacement,
        params, signal
    },
          );
        }, [saveProject])
      }




export const getSaveProjectMutationKey = () => ['saveProject'] as const;

export const useSaveProjectMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useSaveProjectHook>>>, TError,SaveProjectMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useSaveProjectHook>>>, TError,SaveProjectMutationVariables, TContext> => {

const mutationKey = getSaveProjectMutationKey();
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const saveProject =  useSaveProjectHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useSaveProjectHook>>>, SaveProjectMutationVariables> = (props) => {
          const {data,params} = props ?? {};

          return  saveProject(data,params,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SaveProjectMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useSaveProjectHook>>>>
    export type SaveProjectMutationBody = ArticleGroupPlacement[]
    export type SaveProjectMutationError = ErrorType<ProblemDetails>
    export type SaveProjectMutationVariables = {data: ArticleGroupPlacement[];params: SaveProjectParams}

    export const useSaveProject = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useSaveProjectHook>>>, TError,SaveProjectMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useSaveProjectHook>>>,
        TError,
        SaveProjectMutationVariables,
        TContext
      > => {
      return useMutation(useSaveProjectMutationOptions(options), queryClient);
    }
    /**
 * Load a saved project into a forming session. Returns the session ID and article groups so the frontend can join the session and replay placements.
 */
export const useLoadProjectHook = () => {
        const loadProject = useCustomAppClient<LoadProjectResponse>();

        return useCallback((
    projectId: string = '',
 signal?: AbortSignal
) => {


        return loadProject(
          {url: `/projects/${projectId}/load`, method: 'POST', signal
    },
          );
        }, [loadProject])
      }




export const getLoadProjectMutationKey = () => ['loadProject'] as const;

export const useLoadProjectMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useLoadProjectHook>>>, TError,LoadProjectMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useLoadProjectHook>>>, TError,LoadProjectMutationVariables, TContext> => {

const mutationKey = getLoadProjectMutationKey();
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const loadProject =  useLoadProjectHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useLoadProjectHook>>>, LoadProjectMutationVariables> = (props) => {
          const {projectId} = props ?? {};

          return  loadProject(projectId,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type LoadProjectMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useLoadProjectHook>>>>

    export type LoadProjectMutationError = ErrorType<ProblemDetails>
    export type LoadProjectMutationVariables = {projectId?: string}

    export const useLoadProject = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useLoadProjectHook>>>, TError,LoadProjectMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useLoadProjectHook>>>,
        TError,
        LoadProjectMutationVariables,
        TContext
      > => {
      return useMutation(useLoadProjectMutationOptions(options), queryClient);
    }
    /**
 * Permanently delete a saved project
 */
export const useDeleteProjectHook = () => {
        const deleteProject = useCustomAppClient<void>();

        return useCallback((
    projectId: string = '',
 signal?: AbortSignal
) => {


        return deleteProject(
          {url: `/projects/${projectId}`, method: 'DELETE', signal
    },
          );
        }, [deleteProject])
      }




export const getDeleteProjectMutationKey = () => ['deleteProject'] as const;

export const useDeleteProjectMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useDeleteProjectHook>>>, TError,DeleteProjectMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useDeleteProjectHook>>>, TError,DeleteProjectMutationVariables, TContext> => {

const mutationKey = getDeleteProjectMutationKey();
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const deleteProject =  useDeleteProjectHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useDeleteProjectHook>>>, DeleteProjectMutationVariables> = (props) => {
          const {projectId} = props ?? {};

          return  deleteProject(projectId,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteProjectMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDeleteProjectHook>>>>

    export type DeleteProjectMutationError = ErrorType<ProblemDetails>
    export type DeleteProjectMutationVariables = {projectId?: string}

    export const useDeleteProject = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useDeleteProjectHook>>>, TError,DeleteProjectMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useDeleteProjectHook>>>,
        TError,
        DeleteProjectMutationVariables,
        TContext
      > => {
      return useMutation(useDeleteProjectMutationOptions(options), queryClient);
    }
